import { useState, useCallback } from 'react';
import { JobType, ShiftType, JobLocationType, } from '@common/clientServerApi';
const EMPTY_STATE = {
    jobTitle: '',
    jobDescriptionText: '',
    jobType: JobType.FullTime,
    shiftType: ShiftType.StandardBusinessHours,
    locationType: JobLocationType.InPerson,
    location: 'US',
    minExperienceYears: 0,
    mandatorySkills: [],
    responsibilities: [],
    schedule: '',
    salaryMin: '',
    salaryMax: '',
};
const normalizeStringList = (list) => list.map((s) => s.trim()).filter((s) => s.length > 0);
const parseSalaryRange = (min, max, currency = 'USD') => {
    const minN = parseFloat(min);
    const maxN = parseFloat(max);
    if (!Number.isFinite(minN) && !Number.isFinite(maxN))
        return undefined;
    return {
        min: Number.isFinite(minN) ? minN : 0,
        max: Number.isFinite(maxN) ? maxN : Number.isFinite(minN) ? minN : 0,
        currency,
    };
};
export const useJobForm = () => {
    const [jobTitle, setJobTitle] = useState(EMPTY_STATE.jobTitle);
    const [jobDescriptionText, setJobDescriptionText] = useState(EMPTY_STATE.jobDescriptionText);
    const [jobType, setJobType] = useState(EMPTY_STATE.jobType);
    const [shiftType, setShiftType] = useState(EMPTY_STATE.shiftType);
    const [locationType, setLocationType] = useState(EMPTY_STATE.locationType);
    const [location, setLocation] = useState(EMPTY_STATE.location);
    const [minExperienceYears, setMinExperienceYears] = useState(EMPTY_STATE.minExperienceYears);
    const [mandatorySkills, setMandatorySkills] = useState(EMPTY_STATE.mandatorySkills);
    const [responsibilities, setResponsibilities] = useState(EMPTY_STATE.responsibilities);
    const [schedule, setSchedule] = useState(EMPTY_STATE.schedule);
    const [salaryMin, setSalaryMin] = useState(EMPTY_STATE.salaryMin);
    const [salaryMax, setSalaryMax] = useState(EMPTY_STATE.salaryMax);
    const reset = useCallback(() => {
        setJobTitle(EMPTY_STATE.jobTitle);
        setJobDescriptionText(EMPTY_STATE.jobDescriptionText);
        setJobType(EMPTY_STATE.jobType);
        setShiftType(EMPTY_STATE.shiftType);
        setLocationType(EMPTY_STATE.locationType);
        setLocation(EMPTY_STATE.location);
        setMinExperienceYears(EMPTY_STATE.minExperienceYears);
        setMandatorySkills([]);
        setResponsibilities([]);
        setSchedule(EMPTY_STATE.schedule);
        setSalaryMin(EMPTY_STATE.salaryMin);
        setSalaryMax(EMPTY_STATE.salaryMax);
    }, []);
    const loadFromDescription = useCallback((d) => {
        setJobTitle(d.title);
        setJobDescriptionText(d.descriptionText);
        setJobType(d.jobType);
        setShiftType(d.shiftType);
        setLocationType(d.locationType);
        setLocation(d.location);
        setMinExperienceYears(d.minExperienceYears);
        setMandatorySkills(d.requirements ? [...d.requirements] : []);
        setResponsibilities(d.responsibilities ? [...d.responsibilities] : []);
        setSchedule(d.schedule ?? '');
        setSalaryMin(d.salaryRange ? String(d.salaryRange.min) : '');
        setSalaryMax(d.salaryRange ? String(d.salaryRange.max) : '');
    }, []);
    const toJobDescription = useCallback((base) => {
        const responsibilitiesList = normalizeStringList(responsibilities);
        const trimmedSchedule = schedule.trim();
        const currency = base?.salaryRange?.currency ?? 'USD';
        const salaryRange = parseSalaryRange(salaryMin, salaryMax, currency);
        return {
            // Preserve fields we don't expose in the form (experienceLevel,
            // employmentType, benefits, industry) by spreading base first.
            ...(base ?? {}),
            title: jobTitle.trim(),
            descriptionText: jobDescriptionText.trim(),
            location: location.trim(),
            jobType,
            shiftType,
            locationType,
            minExperienceYears,
            requirements: mandatorySkills.length > 0 ? [...mandatorySkills] : [],
            responsibilities: responsibilitiesList.length > 0 ? responsibilitiesList : undefined,
            schedule: trimmedSchedule || undefined,
            salaryRange,
        };
    }, [
        jobTitle,
        jobDescriptionText,
        location,
        jobType,
        shiftType,
        locationType,
        minExperienceYears,
        mandatorySkills,
        responsibilities,
        schedule,
        salaryMin,
        salaryMax,
    ]);
    return {
        jobTitle,
        jobDescriptionText,
        jobType,
        shiftType,
        locationType,
        location,
        minExperienceYears,
        mandatorySkills,
        responsibilities,
        schedule,
        salaryMin,
        salaryMax,
        setJobTitle,
        setJobDescriptionText,
        setJobType,
        setShiftType,
        setLocationType,
        setLocation,
        setMinExperienceYears,
        setMandatorySkills,
        setResponsibilities,
        setSchedule,
        setSalaryMin,
        setSalaryMax,
        reset,
        loadFromDescription,
        toJobDescription,
    };
};
